import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'

interface BulkActionBarProps {
  /** Live effective selection count (page size in explicit mode, server total in select-all). */
  count: number
  onClear: () => void
  /** Bulk action controls (delete, re-categorize, add-to-report, download) plug in here. */
  children?: React.ReactNode
}

/**
 * Floating bar pinned to the bottom of the viewport whenever a selection is
 * non-empty. Shows the live count, the bulk action buttons, and a clear
 * affordance. The specific actions are supplied by the caller as children so
 * each bulk-action feature can own its own control.
 */
export const BulkActionBar = ({ count, onClear, children }: BulkActionBarProps) => {
  const { t } = useTranslation()
  return (
    <div
      className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-[min(95%,56rem)] flex-wrap items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 shadow-lg"
      role="region"
      aria-label={t('bulkActions.barLabel')}
      data-testid="bulk-action-bar"
    >
      <span className="font-semibold text-text-primary" data-testid="bulk-selected-count">
        {t('bulkActions.selectedCount', { count })}
      </span>
      <div className="flex flex-1 flex-wrap items-center gap-2">{children}</div>
      <Button
        variant="text"
        tone="neutral"
        size="sm"
        onClick={onClear}
        data-testid="bulk-clear-selection"
      >
        <XMarkIcon className="mr-1 h-4 w-4" aria-hidden="true" />
        {t('bulkActions.clearSelection')}
      </Button>
    </div>
  )
}
